import {
  Header as SundayHeader,
  ThemeProvider as SundayThemeProvider,
} from "@sunday/component";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { MouseEvent, useState } from "react";
import ConfirmDialog from "src/components/Common/ConfirmDialog/ConfirmDialog";
import {
  ICTH_LOGO,
  ICTH_THEME,
} from "src/components/Common/ICTHLayout/constants";
import { buildICTHHeaderMenus } from "src/components/Common/ICTHLayout/navigation";
import { HeaderMenu, IHeader, Language } from "../IHeader";

type ICTHHeaderProps = Pick<
  IHeader,
  "content" | "language" | "onChangeLanguage" | "showSwitchLanguage"
> & {
  menus: HeaderMenu | undefined;
  showMenus: boolean;
};

const ICTHHeader = ({
  content,
  language,
  menus,
  onChangeLanguage,
  showMenus,
  showSwitchLanguage,
}: ICTHHeaderProps): JSX.Element => {
  const { t } = useTranslation("layout");
  const router = useRouter();
  const [isLeaveSiteDialogVisible, setIsLeaveSiteDialogVisible] =
    useState(false);
  const [pendingLogoHref, setPendingLogoHref] = useState(ICTH_LOGO.href);
  const availableLanguages: Language[] =
    content?.supportedLangs?.map((lang) => lang.code as Language) || [];
  const headerMenus = showMenus
    ? buildICTHHeaderMenus({
        language,
        translate: t,
        customMenus: menus,
      })
    : [];
  const isApplicationFormPage =
    router.pathname.includes("/application-form/") ||
    router.asPath.includes("/application-form/");

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!isApplicationFormPage) {
      return;
    }

    const { target } = event;

    if (!(target instanceof Element)) {
      return;
    }

    const logoLink = target.closest("a");

    if (
      !logoLink ||
      logoLink.getAttribute("href") !== ICTH_LOGO.href ||
      !logoLink.querySelector(`img[alt="${ICTH_LOGO.alt}"]`)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setPendingLogoHref(logoLink.getAttribute("href") || ICTH_LOGO.href);
    setIsLeaveSiteDialogVisible(true);
  };

  const handleLeaveSite = () => {
    window.location.assign(pendingLogoHref);
  };

  const handleStayOnSite = () => {
    setIsLeaveSiteDialogVisible(false);
  };

  return (
    <SundayThemeProvider theme={ICTH_THEME}>
      <div onClickCapture={handleClickCapture}>
        <SundayHeader
          logoSrc={ICTH_LOGO.src}
          logoHref={ICTH_LOGO.href}
          logoAlt={ICTH_LOGO.alt}
          menus={headerMenus}
          language={language}
          availableLanguages={availableLanguages}
          showLanguageSwitcher={showSwitchLanguage}
          onChangeLanguage={onChangeLanguage}
          showLoginSignup={false}
          className="icth-header"
        />
      </div>
      {isLeaveSiteDialogVisible && (
        <ConfirmDialog
          appearance="ice-cream"
          centered
          isVisible={isLeaveSiteDialogVisible}
          title={t("leaveSiteDialog.title", {
            defaultValue: "Leave this site?",
          })}
          description={t("leaveSiteDialog.description", {
            defaultValue: "The information that you input may\nnot be saved.",
          })}
          confirmText={t("leaveSiteDialog.confirmText", {
            defaultValue: "Leave",
          })}
          rejectText={t("leaveSiteDialog.rejectText", {
            defaultValue: "Stay",
          })}
          onConfirm={handleLeaveSite}
          onReject={handleStayOnSite}
          confirmButtonProps={{
            type: "default",
          }}
          rejectButtonProps={{
            type: "primary",
          }}
        />
      )}
    </SundayThemeProvider>
  );
};

export default ICTHHeader;
